import Image from "next/image";
import Link from "next/link";
import type { BrowseMode, ListingSummary } from "@/lib/browse/getHomeBrowseData";
import { isSavedMediaVariantUrl } from "@/lib/images/resolveMediaAssetUrl";
import { getCustomerListingUrl, getListingUrl } from "@/lib/ids/publicRefs";
import { resolveListingCardImageUrl } from "@/lib/listingPhotos";
import { calculateListingPricing } from "@/lib/pricing";
import { getListingCategoryPlaceholder } from "@/lib/taxonomy/placeholders";
import styles from "./HomepageEditorial.module.css";

function formatListingPrice(listing: ListingSummary) {
  const suppliedCents = Number(listing?.finalPriceCents);
  const cents =
    Number.isFinite(suppliedCents) && suppliedCents > 0
      ? suppliedCents
      : calculateListingPricing(listing?.price).finalPriceCents;

  if (!Number.isFinite(cents) || cents <= 0) return "Price TBD";
  return (cents / 100).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export default function EditorialListingCard({
  listing,
  mode,
}: {
  listing: ListingSummary;
  mode: BrowseMode;
}) {
  const href =
    mode === "customer" ? getCustomerListingUrl(listing) : getListingUrl(listing);
  const imageSrc =
    resolveListingCardImageUrl(listing) || getListingCategoryPlaceholder(listing);
  const businessName =
    String(listing?.business_name || "").trim() || "Local business";

  return (
    <Link
      href={href}
      prefetch={false}
      className={styles.listingCard}
      title={listing.title || "Untitled listing"}
      aria-label={`${listing.title || "Untitled listing"}, ${formatListingPrice(
        listing
      )}, from ${businessName}`}
    >
      <span className={styles.listingImage}>
        <Image
          src={imageSrc}
          alt=""
          fill
          sizes="72px"
          className="object-contain object-center p-1.5 transition-transform duration-300 group-hover:scale-[1.035]"
          unoptimized={isSavedMediaVariantUrl(imageSrc)}
        />
      </span>
      <span className={styles.listingCopy}>
        <span className={styles.listingPrice}>{formatListingPrice(listing)}</span>
        <span className={styles.listingTitle}>{listing.title || "Untitled listing"}</span>
        <span className={styles.listingBusiness}>{businessName}</span>
      </span>
      <span className={styles.listingArrow} aria-hidden="true">
        →
      </span>
    </Link>
  );
}
